/**
 * Comprehensive Prompt Builder for AI Insights
 *
 * Builds prompts using data from ALL three API endpoints:
 * - Clubs (organization metadata)
 * - Valuations (financial data)
 * - Player Sales (transfer data)
 */

import { ComprehensiveReportData, ValuationData } from '@/lib/platform/types/plugins';

export function buildComprehensivePrompt(
  data: ComprehensiveReportData,
  locale: 'en' | 'da'
): string {
  const { club, valuations, playerSales, summary } = data;

  const language = locale === 'da' ? 'Danish' : 'English';

  if (valuations.length === 0) {
    throw new Error('No valuations available for insights');
  }

  const current = valuations[0];
  const previous = valuations[1] || null;

  // Calculate player sales metrics
  const totalSalesValue = playerSales.reduce((sum, sale) => sum + sale.sale_amount, 0);
  const averageSalePrice = playerSales.length > 0 ? totalSalesValue / playerSales.length : 0;

  // Recent sales (last 3)
  const recentSales = playerSales.slice(0, 3);

  // Value change
  const valueChange = previous
    ? ((current.final_total_value - previous.final_total_value) / previous.final_total_value) * 100
    : 0;

  return `You are a senior sports business analyst specializing in sponsorship and attention valuation.
Your task is to generate exactly 3 **actionable insights** from this football club's complete business data below.
Focus on providing concise, data-driven business intelligence relevant for sponsorship and partnership strategy.

**CLUB INFORMATION:**
Name: ${club.name}
Country: ${club.country}
League: ${club.league}
Tier: ${club.tier}

**CURRENT VALUATION (${current.valuation_date}):**
Total Attention Valuation (Total Value): ${formatCurrency(current.final_total_value)}
Sales Value: ${formatCurrency(current.calculated_sales_value)}
Media Value: ${formatCurrency(current.calculated_media_value)}
Qualitative Multiplier: ${current.qualitative_multiplier}x

${
  previous
    ? `**PREVIOUS VALUATION (${previous.valuation_date}):**
Total Attention Valuation (Total Value): ${formatCurrency(previous.final_total_value)}
Sales Value: ${formatCurrency(previous.calculated_sales_value)}
Media Value: ${formatCurrency(previous.calculated_media_value)}
Change: ${formatPercentage(valueChange)} (${valueChange > 0 ? 'up' : 'down'})
`
    : ''
}

**PLAYER SALES ACTIVITY:**
Total Sales: ${playerSales.length}
Total Sales Revenue: ${formatCurrency(totalSalesValue)}
Average Sale Price: ${formatCurrency(averageSalePrice)}

${
  recentSales.length > 0
    ? `**RECENT TRANSFERS:**
${recentSales
  .map(
    (sale) =>
      `- ${sale.player_name} to ${sale.buyer_club}: ${formatCurrency(sale.sale_amount)} (${sale.sale_date})`
  )
  .join('\n')}
`
    : 'No recent transfers recorded.'
}

**OVERALL TREND:**
Total Valuations: ${summary.totalValuations}
Average Value: ${formatCurrency(summary.averageValue)}
Trend: ${summary.trend === 'up' ? '📈 Upward' : summary.trend === 'down' ? '📉 Downward' : '➡️ Stable'}

Generate exactly 3 insights in ${language} (Danish or English). Each insight must:
1. Combine data from valuations, player sales, and club context
2. Provide actionable business intelligence
3. Be specific and data-driven
4. Be concise (1-2 sentences max)
5. Consider the club's league tier and country market

**Focus areas:**
- Value change trends and primary drivers
- Revenue composition and generation patterns (sales vs media value)
- Player trading strategy effectiveness and ROI
- Market position in ${club.league} (Tier ${club.tier})
- Value growth trajectory and sustainability
- Qualitative multiplier impact (brand perception, fan sentiment)
- Risk factors or opportunities based on transfer activity

**Output format:**
Return ONLY a valid JSON array:
[
  {
    "type": "positive" | "negative" | "neutral",
    "title": "Brief insight title (3-5 words)",
    "description": "Detailed insight description combining multiple data points (1-2 sentences)"
  },
  {
    "type": "positive" | "negative" | "neutral",
    "title": "Brief insight title (3-5 words)",
    "description": "Detailed insight description combining multiple data points (1-2 sentences)"
  },
  {
    "type": "positive" | "negative" | "neutral",
    "title": "Brief insight title (3-5 words)",
    "description": "Detailed insight description combining multiple data points (1-2 sentences)"
  }
]

IMPORTANT: Return ONLY the JSON array, no additional text or explanation.`;
}

function formatCurrency(amount: number): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount);
}

function formatPercentage(value: number): string {
  const sign = value >= 0 ? '+' : '';
  return `${sign}${value.toFixed(1)}%`;
}

/**
 * Get the default prompt template
 */
export function getDefaultPromptTemplate(): string {
  return `You are a senior sports business analyst specializing in sponsorship and attention valuation.
Your task is to generate 3–5 **actionable insights** from the club's valuation data below.
Focus on providing concise, data-driven business intelligence relevant for sponsorship and partnership strategy.

**CURRENT VALUATION ({{CURRENT_DATE}}):**
Total Attention Valuation (Total Value): {{CURRENT_TOTAL}}
Sales Value: {{CURRENT_SALES}}
Media Value: {{CURRENT_MEDIA}}
Qualitative Multiplier: {{CURRENT_MULTIPLIER}}x

{{PREVIOUS_SECTION}}

Generate 3–5 insights in {{LANGUAGE}} (Danish or English). Each insight must:
1. Be grounded in the valuation data above
2. Provide actionable business intelligence
3. Be specific and data-driven
4. Be concise (1–2 sentences max)

**Focus areas:**
- Value change trends (growth or decline)
- Revenue composition (balance between sales and media value)
- Qualitative multiplier impact (brand perception, fan sentiment)
- Strategic recommendations for sponsors or club management

**Output format:**
Return ONLY a valid JSON array:
[
  {
    "type": "positive" | "negative" | "neutral",
    "title": "Brief title",
    "description": "Concise insight (1-2 sentences)"
  }
]

Generate 3–5 insights. Return ONLY the JSON array, no additional text.

**Technical Parameters:**
- Timeout: 15 seconds
- Max tokens: 1000
- Temperature: 0.7`;
}

/**
 * Simple prompt builder for valuations only
 * Used when we only have valuation data (e.g., from Reports page)
 */
export function buildPrompt(
  current: ValuationData,
  previous: ValuationData | null,
  locale: 'en' | 'da',
  customPrompt?: string
): string {
  const language = locale === 'da' ? 'Danish' : 'English';

  // Calculate changes if previous exists
  const valueChange = previous
    ? ((current.final_total_value - previous.final_total_value) / previous.final_total_value) * 100
    : 0;

  const salesChange = previous
    ? ((current.calculated_sales_value - previous.calculated_sales_value) /
        previous.calculated_sales_value) *
      100
    : 0;

  const mediaChange = previous
    ? ((current.calculated_media_value - previous.calculated_media_value) /
        previous.calculated_media_value) *
      100
    : 0;

  // If custom prompt provided, use it with placeholder replacement
  if (customPrompt) {
    const previousSection = previous
      ? `**PREVIOUS VALUATION (${previous.valuation_date}):**
Total Attention Valuation (Total Value): ${formatCurrency(previous.final_total_value)} (${formatPercentage(valueChange)} change)
Sales Value: ${formatCurrency(previous.calculated_sales_value)} (${formatPercentage(salesChange)} change)
Media Value: ${formatCurrency(previous.calculated_media_value)} (${formatPercentage(mediaChange)} change)
Qualitative Multiplier: ${previous.qualitative_multiplier}x`
      : 'No previous valuation available for comparison.';

    return customPrompt
      .replace(/\{\{CURRENT_DATE\}\}/g, current.valuation_date)
      .replace(/\{\{CURRENT_TOTAL\}\}/g, formatCurrency(current.final_total_value))
      .replace(/\{\{CURRENT_SALES\}\}/g, formatCurrency(current.calculated_sales_value))
      .replace(/\{\{CURRENT_MEDIA\}\}/g, formatCurrency(current.calculated_media_value))
      .replace(/\{\{CURRENT_MULTIPLIER\}\}/g, current.qualitative_multiplier.toString())
      .replace(/\{\{PREVIOUS_SECTION\}\}/g, previousSection)
      .replace(/\{\{LANGUAGE\}\}/g, language);
  }

  // Include player sales data if available (not available in simple buildPrompt - only in comprehensive)
  const currentPlayerSales: Array<{ player_name: string; sale_value: number }> = [];
  const totalCurrentSales = 0;
  const playerSalesSection =
    currentPlayerSales.length > 0
      ? `\n**PLAYER SALES (Recent Transfers):**
Total Sales: ${currentPlayerSales.length}
Total Sales Revenue: ${formatCurrency(totalCurrentSales)}
Average Sale Price: ${formatCurrency(currentPlayerSales.length > 0 ? totalCurrentSales / currentPlayerSales.length : 0)}
${currentPlayerSales
  .slice(0, 3)
  .map((sale) => `- ${sale.player_name}: ${formatCurrency(sale.sale_value)}`)
  .join('\n')}`
      : '';

  // Default prompt
  return `You are a senior sports business analyst specializing in sponsorship and attention valuation.
Your task is to generate 3–5 **actionable insights** from the club's valuation data below.
Focus on providing concise, data-driven business intelligence relevant for sponsorship and partnership strategy.

**CURRENT VALUATION (${current.valuation_date}):**
Total Attention Valuation (Total Value): ${formatCurrency(current.final_total_value)}
Sales Value: ${formatCurrency(current.calculated_sales_value)}
Media Value: ${formatCurrency(current.calculated_media_value)}
Qualitative Multiplier: ${current.qualitative_multiplier}x
${playerSalesSection}

${
  previous
    ? `**PREVIOUS VALUATION (${previous.valuation_date}):**
Total Attention Valuation (Total Value): ${formatCurrency(previous.final_total_value)} (${formatPercentage(valueChange)} change)
Sales Value: ${formatCurrency(previous.calculated_sales_value)} (${formatPercentage(salesChange)} change)
Media Value: ${formatCurrency(previous.calculated_media_value)} (${formatPercentage(mediaChange)} change)
Qualitative Multiplier: ${previous.qualitative_multiplier}x
`
    : 'No previous valuation available for comparison.'
}

Generate 3–5 insights in ${language} (Danish or English). Each insight must:
1. Be grounded in the valuation data above
2. Provide actionable business intelligence
3. Be specific and data-driven
4. Be concise (1–2 sentences max)

**Focus areas:**
- Value change trends (growth or decline)
- Revenue composition (balance between sales and media value)
- Player trading strategy effectiveness and ROI (if player sales data available)
- Qualitative multiplier impact (brand perception, fan sentiment)
- Strategic recommendations for sponsors or club management

**Output format:**
Return ONLY a valid JSON array:
[
  {
    "type": "positive" | "negative" | "neutral",
    "title": "Brief title",
    "description": "Concise insight (1-2 sentences)"
  }
]

Generate 3–5 insights. Return ONLY the JSON array, no additional text.

**Technical Parameters:**
- Timeout: 15 seconds
- Max tokens: 1000
- Temperature: 0.7`;
}
